import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  NavigationEnd,
  NavigationError,
  PRIMARY_OUTLET,
  Route,
  Router,
  UrlMatchResult,
  defaultUrlMatcher,
} from '@angular/router';
import { Location } from '@angular/common';
import { filter, fromEvent, Subject, interval } from 'rxjs';
import {
  distinctUntilChanged,
  takeUntil,
  map,
  debounceTime,
} from 'rxjs/operators';
import { SubscriptionLike } from 'rxjs';
import { Angulartics2GoogleGlobalSiteTag } from 'angulartics2';
import { lt } from 'semver';
import * as crypto from 'crypto-js';

import { DialogRef, DialogService, TypedDialog } from 'shared/ui';
import { OthersBookshelfOpenerService, SocketService } from 'shared/services';

import { environment } from '../environments/environment';

import { NativeEnvironmentService } from './services/native-environment.service';

import { OthersBookshelfDialogComponent } from './dialogs/others-bookshelf-dialog/others-bookshelf-dialog.component';
import { OfflineDialogComponent } from './dialogs/offline-dialog/offline-dialog.component';
import { UpdateNotificationDialogComponent } from './dialogs/update-notification-dialog/update-notification-dialog.component';

const WEB_ROUTES: Route[] = [
  {
    path: 'bookshelf/:userId',
  },
];

const EncryptionKey = 'statistics';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy {
  private _offlineDialogRef?: DialogRef<TypedDialog<void, void>, void>;
  private _unsubscriber = new Subject<void>();
  private _lastSentUrl?: string;
  private _locationChangeSubscription?: SubscriptionLike;
  private _urlCheckInterval?: SubscriptionLike;
  private _urlSendSubject = new Subject<string>();
  private _urlSendSubscription?: SubscriptionLike;

  constructor(
    private _router: Router,
    private _location: Location,
    private _dialogService: DialogService,
    private _othersBookshelfOpenerService: OthersBookshelfOpenerService,
    private _socketService: SocketService,
    angulartics: Angulartics2GoogleGlobalSiteTag,
    private _nativeEnvironmentService: NativeEnvironmentService
  ) {
    environment.production && angulartics.startTracking();
  }

  ngOnInit(): void {
    this._socketService.createSocket(environment.wsServerOrigin);

    this._othersBookshelfOpenerService.isApp = true;

    // URL 변경 감지하여 웹소켓으로 전송 (Router 이벤트)
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._unsubscriber)
      )
      .subscribe((event) => {
        const navigationEnd = event as NavigationEnd;
        const url = navigationEnd.urlAfterRedirects || navigationEnd.url;
        this._sendUrlToWebSocket(url);
      });

    // Location 변경 감지 (popstate 이벤트 - 브라우저 뒤로/앞으로 버튼)
    this._locationChangeSubscription = this._location.subscribe(() => {
      const url = this._location.path();
      this._sendUrlToWebSocket(url);
    });

    // popstate 이벤트 직접 리스닝 (뷰어에서 Location.go() 사용 시 감지)
    fromEvent<PopStateEvent>(window, 'popstate')
      .pipe(takeUntil(this._unsubscriber))
      .subscribe(() => {
        const url = this._location.path();
        this._sendUrlToWebSocket(url);
      });

    // 주기적으로 URL 체크 (뷰어에서 Location.go()나 replaceState() 사용 시 감지)
    this._urlCheckInterval = interval(500)
      .pipe(
        takeUntil(this._unsubscriber),
        map(() => this._location.path()),
        distinctUntilChanged()
      )
      .subscribe((url) => {
        this._sendUrlToWebSocket(url);
      });

    // 최초 연결·자동 재연결 시 현재 URL 통계 전송 (재연결 시에도 _lastSentUrl 때문에 막히지 않도록 매번 초기화)
    const socket = this._socketService.getSocket();
    socket.on('connect', () => {
      this._lastSentUrl = undefined;
      this._sendUrlToWebSocket(this._location.path());
    });

    // URL 전송 요청을 debounce하여 처리 (1000ms 내 여러 호출을 하나로 합침)
    this._urlSendSubscription = this._urlSendSubject
      .pipe(debounceTime(1000), takeUntil(this._unsubscriber))
      .subscribe((url) => {
        this._doSendUrlToWebSocket(url);
      });

    this._router.events
      .pipe(
        // tap((event) => {
        //   console.warn(event);
        // }),
        filter(
          (event) =>
            event instanceof NavigationError && event.error?.code === 4002
        )
      )
      .subscribe((event) => {
        this._onNavigationError(event as NavigationError);
      });

    fromEvent(window, 'offline').subscribe(() => {
      if (this._offlineDialogRef) {
        return;
      }

      this._offlineDialogRef = this._dialogService.open(OfflineDialogComponent);

      this._offlineDialogRef.afterClosed().subscribe(() => {
        this._offlineDialogRef = undefined;
      });
    });

    fromEvent(window, 'online').subscribe(() => {
      this._offlineDialogRef?.close();
    });

    // 안드로이드와 IOS에서 버전 체크
    if (this._nativeEnvironmentService.version) {
      const os = this._nativeEnvironmentService.os;
      const currentVersion = this._nativeEnvironmentService.version;

      const minRequiredVersion = os === 'ios' ? '1.1.5' : '1.1.5';

      if (lt(currentVersion, minRequiredVersion)) {
        this.openAppUpdateNotiDialog();
      }
    }
  }

  openAppUpdateNotiDialog(): void {
    this._dialogService.open(UpdateNotificationDialogComponent);
  }

  private _onNavigationError(event: NavigationError): void {
    const segmentGroup = this._router.parseUrl(event.url).root.children[
      PRIMARY_OUTLET
    ];

    for (const route of WEB_ROUTES) {
      const matchResult = defaultUrlMatcher(
        segmentGroup.segments,
        segmentGroup,
        route
      );

      if (matchResult) {
        this._handleWebRouteMatchResult(matchResult);
        return;
      }
    }

    window.open(environment.serviceOrigin + event.url, '_blank');
    return;
  }

  private _handleWebRouteMatchResult(matchResult: UrlMatchResult): void {
    // 타인의 서재
    if (
      matchResult.consumed.length >= 2 &&
      matchResult.consumed[0].path === 'bookshelf' &&
      matchResult.posParams?.['userId'] != null
    ) {
      this._openUserBookshelfDialog(matchResult.posParams['userId'].path);
      return;
    }
  }

  private _openUserBookshelfDialog(userId: string): void {
    this._dialogService.open(OthersBookshelfDialogComponent, {
      data: { userId },
    });
  }

  private _sendUrlToWebSocket(url: string): void {
    // Subject를 통해 debounce 처리
    this._urlSendSubject.next(url);
  }

  private _doSendUrlToWebSocket(url: string): void {
    try {
      const socket = this._socketService.getSocket();
      const isConnected = socket.connected;

      if (!isConnected) {
        console.warn('Socket not connected, skipping URL send');
        return;
      }

      // 중복 전송 방지 (소켓이 연결된 경우에만 체크)
      if (this._lastSentUrl === url) {
        return;
      }

      const data = crypto.AES.encrypt(url, EncryptionKey).toString();
      const message = JSON.stringify({
        cmd: 'statistics',
        data: data,
      });

      socket.emit('message', message);

      // 전송 성공 후에만 마지막 URL 업데이트
      this._lastSentUrl = url;
    } catch (error) {
      console.error('Failed to send URL to websocket', error);
    }
  }

  ngOnDestroy(): void {
    this._unsubscriber.next();
    this._unsubscriber.complete();
    this._locationChangeSubscription?.unsubscribe();
    this._urlCheckInterval?.unsubscribe();
    this._urlSendSubscription?.unsubscribe();
  }
}
